// Shape and rules of one vehicle slot's assignment, shared by the editor, the
// confirmation modal and the request payload. Mirrors `routes/utils/assignment.js`
// on the server, which is the authority — anything here is only for the form.

export const FLEET = "fleet";
// Still one of our own vehicles, only not the class that was booked.
export const UPGRADE = "upgrade";
export const CUSTOM = "custom";
export const NONE = "none";

// A booking stores its assignment on each bookingInfo row, populated to a
// document on read and a bare id on write.
export const idOf = (value) => (value && typeof value === "object" ? value._id : value) || "";

export const MAX_TEXT_LENGTH = 120;

// Our drivers, and the partner drivers standing in for them, are in Japan, so the
// country code is fixed furniture in the phone field rather than something to
// type or delete. The input holds only the national part: the code comes off on
// read and goes back on write, and a field with no national part is no number.
export const PHONE_COUNTRY_CODE = "+81";

export const stripPhoneCountryCode = (value) => {
    const trimmed = (value || "").trim();
    return trimmed.startsWith(PHONE_COUNTRY_CODE)
        ? trimmed.slice(PHONE_COUNTRY_CODE.length).trim()
        : trimmed;
};

export const withPhoneCountryCode = (value) => {
    const national = stripPhoneCountryCode(value);
    return national ? `${PHONE_COUNTRY_CODE} ${national}` : "";
};

export const emptyAssignment = () => ({
    vehicleAssignmentType: FLEET,
    vehicle: "",
    customVehicleName: "",
    customVehicleRegistration: "",
    driverAssignmentType: FLEET,
    driver: "",
    customDriverName: "",
    customDriverPhone: ""
});

// Slots saved before free-text assignment existed carry no type at all, and
// every one of them was a fleet assignment.
export const assignmentFromSlot = (slot) => ({
    vehicleAssignmentType:
        slot?.vehicleAssignmentType === CUSTOM || slot?.vehicleAssignmentType === UPGRADE
            ? slot.vehicleAssignmentType
            : FLEET,
    vehicle: idOf(slot?.vehicle),
    customVehicleName: slot?.customVehicle?.name || "",
    customVehicleRegistration: slot?.customVehicle?.registration || "",
    driverAssignmentType:
        slot?.driverAssignmentType === CUSTOM || slot?.driverAssignmentType === NONE
            ? slot.driverAssignmentType
            : FLEET,
    driver: idOf(slot?.driver),
    customDriverName: slot?.customDriver?.name || "",
    customDriverPhone: stripPhoneCountryCode(slot?.customDriver?.phone)
});

export const isVehicleFilled = (assignment) =>
    assignment?.vehicleAssignmentType === CUSTOM
        ? Boolean(assignment?.customVehicleName?.trim())
        // Both fleet and upgrade point at one of our own vehicles.
        : Boolean(assignment?.vehicle);

// A client-supplied driver is a decision, not a gap, so it counts as filled in.
export const isDriverFilled = (assignment) => {
    if (assignment?.driverAssignmentType === NONE) return true;
    if (assignment?.driverAssignmentType === CUSTOM) return Boolean(assignment?.customDriverName?.trim());
    return Boolean(assignment?.driver);
};

export const isAssignmentFilled = (assignment) =>
    isVehicleFilled(assignment) && isDriverFilled(assignment);

// Only the fields that actually travel to the server, so switching to a mode and
// back does not read as an unsaved change.
export const assignmentSignature = (assignment) => {
    const vehicleType = assignment?.vehicleAssignmentType;
    const isCustomVehicle = vehicleType === CUSTOM;
    const driverType = assignment?.driverAssignmentType;

    return JSON.stringify([
        vehicleType,
        isCustomVehicle ? assignment?.customVehicleName?.trim() : assignment?.vehicle,
        isCustomVehicle ? assignment?.customVehicleRegistration?.trim() : "",
        driverType,
        driverType === CUSTOM ? assignment?.customDriverName?.trim() : driverType === FLEET ? assignment?.driver : "",
        driverType === CUSTOM ? withPhoneCountryCode(assignment?.customDriverPhone) : ""
    ]);
};

export const toAssignmentPayload = (assignment, index) => {
    const vehicleAssignmentType = assignment.vehicleAssignmentType;
    const driverAssignmentType = assignment.driverAssignmentType;

    return {
        index,
        vehicleAssignmentType,
        vehicle: vehicleAssignmentType === CUSTOM ? undefined : assignment.vehicle,
        customVehicle:
            vehicleAssignmentType === CUSTOM
                ? {
                    name: assignment.customVehicleName.trim(),
                    registration: assignment.customVehicleRegistration.trim()
                }
                : undefined,
        driverAssignmentType,
        driver: driverAssignmentType === FLEET ? assignment.driver : undefined,
        customDriver:
            driverAssignmentType === CUSTOM
                ? {
                    name: assignment.customDriverName.trim(),
                    phone: withPhoneCountryCode(assignment.customDriverPhone)
                }
                : undefined
    };
};

/**
 * Flattens an assignment to the labels the confirmation modal shows, resolving
 * fleet ids against the option lists the editor already loaded.
 */
export const describeAssignment = (assignment, vehicleOptions, driverOptions) => {
    const isCustomVehicle = assignment.vehicleAssignmentType === CUSTOM;
    const isFreeUpgrade = assignment.vehicleAssignmentType === UPGRADE;
    const isCustomDriver = assignment.driverAssignmentType === CUSTOM;
    const isDriverUnassigned = assignment.driverAssignmentType === NONE;

    const vehicleOption = vehicleOptions.find((option) => option.value === assignment.vehicle);
    const driverOption = driverOptions.find((option) => option.value === assignment.driver);

    return {
        isCustomVehicle,
        isFreeUpgrade,
        isCustomDriver,
        isDriverUnassigned,
        vehicleName: (isCustomVehicle ? assignment.customVehicleName : vehicleOption?.vehicleName) || "-",
        registrationNumber:
            (isCustomVehicle ? assignment.customVehicleRegistration : vehicleOption?.registrationNumber) || "-",
        driverName: isDriverUnassigned
            ? "No driver — provided by the client"
            : (isCustomDriver ? assignment.customDriverName : driverOption?.driverName) || "-",
        driverDetail: isDriverUnassigned
            ? ""
            : (isCustomDriver ? withPhoneCountryCode(assignment.customDriverPhone) : driverOption?.driverEmail) || "-"
    };
};

// ─── Meet & assist staff ──────────────────────────────────────────────────────
// Meet & assist is worked by people standing in the terminal, not by the vehicle,
// so the staff are assigned once for the whole booking rather than per slot —
// which is why they live beside the assignments here rather than inside one.

// The two products; anything else ("none", or a blank row) is no service.
const MEET_ASSIST_SERVICES = ["meetAssist", "vipMeetAssist"];

const MEET_ASSIST_LABELS = {
    meetAssist: "Meet & Assist",
    vipMeetAssist: "VIP Meet & Assist",
};

export const MAX_MEET_ASSIST_STAFFS = 10;

export const hasMeetAssistService = (booking) =>
    (booking?.meetAssist || []).some((item) => MEET_ASSIST_SERVICES.includes(item?.meetAssistName));

// "VIP Meet & Assist (Arrival), Meet & Assist (Departure)" — what was actually
// bought, so the admin knows how many people the booking really needs.
export const meetAssistServiceLabel = (booking) =>
    (booking?.meetAssist || [])
        .filter((item) => MEET_ASSIST_SERVICES.includes(item?.meetAssistName))
        .map(
            (item) =>
                `${MEET_ASSIST_LABELS[item.meetAssistName]} (${item?.meetAssistType === "departure" ? "Departure" : "Arrival"
                })`
        )
        .join(", ");

export const emptyMeetAssistStaff = () => ({ name: "", phone: "" });

// The form always shows at least one row, so a booking with nobody assigned yet
// opens on an empty person rather than on nothing at all.
export const meetAssistStaffsFromBooking = (booking) => {
    const saved = (booking?.meetAssistStaffs || []).map((staff) => ({
        name: staff?.name || "",
        phone: stripPhoneCountryCode(staff?.phone),
    }));

    return saved.length ? saved : [emptyMeetAssistStaff()];
};

const isBlankStaff = (staff) => !staff?.name?.trim() && !staff?.phone?.trim();

// A row left entirely blank is an unused row and simply drops out. One carrying
// a phone number and no name is a person the admin started to type and never
// named, which is the only thing here that holds the form back — staff can be
// filled in later, without blocking the vehicles that are already settled.
export const hasIncompleteMeetAssistStaff = (staffs) =>
    (staffs || []).some((staff) => !isBlankStaff(staff) && !staff.name?.trim());

export const toMeetAssistStaffsPayload = (staffs) =>
    (staffs || [])
        .filter((staff) => !isBlankStaff(staff))
        .map((staff) => ({
            name: staff.name.trim(),
            phone: withPhoneCountryCode(staff.phone),
        }));

// Only what would actually be saved, so adding and then clearing a row does not
// read as an unsaved change.
export const meetAssistStaffsSignature = (staffs) => JSON.stringify(toMeetAssistStaffsPayload(staffs));

const isOfBookedType = (option, bookedVehicleTypeId) => {
    const bookedTypeId = String(idOf(bookedVehicleTypeId) || "");
    return Boolean(bookedTypeId) && String(option.vehicleTypeId || "") === bookedTypeId;
};

/**
 * Which of our vehicles the picker offers, which is the whole point of the mode:
 * "fleet" holds the admin to the class that was booked, "upgrade" opens the list
 * up so another class can be given instead.
 */
export const vehicleOptionsForMode = (options, mode, bookedVehicleTypeId) => {
    console.log(options)
    console.log(bookedVehicleTypeId)
    return mode === UPGRADE ? options : options.filter((option) => isOfBookedType(option, bookedVehicleTypeId));
}

/**
 * Switching back to "from our fleet" must not keep a vehicle of another class
 * selected — it would be submitted as if it were the class booked. A vehicle we
 * cannot place (options still loading, or an inactive vehicle saved earlier) is
 * left alone rather than silently dropped.
 */
export const isOfBookedVehicleType = (vehicleId, options, bookedVehicleTypeId) => {
    if (!vehicleId || !options.length) return true;

    const option = options.find((item) => item.value === vehicleId);
    return !option || isOfBookedType(option, bookedVehicleTypeId);
};
